/**
 * Payment reminders — every bill still owed, nudged in one go.
 *
 * Owner, Oct 2 2026: "send follow ups on all billing". The "Send reminder"
 * button inside a bill stays; this is the same text for all of them at once,
 * after a look (his Oct 1 rule: "review first"). One row per open bill: who,
 * how much is still owed, how long it has been open, when it was last nudged.
 * A bill reminded in the last 3 days starts unticked; one that cannot be
 * texted says why and cannot be ticked. An autopay bill here is one the card
 * did not pay (declined, or never charged — the card is tried once, at
 * Review & send, and never again on its own), so it is ticked like any other
 * and its note says so, never "will be charged".
 * He taps "Send N reminders" and reads what happened, row by row
 * (server/services/billFollowUp). Shows only when a bill is open.
 * Oct 26 2026 ("the reminder texts should be collapsed"): folded to one line —
 * "3 open bills, oldest 18 days · $X owed" — that opens on a tap (FoldCard).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Bell } from 'lucide-react';
import { api } from '../services/api';
import FoldCard from './FoldCard';

interface FollowUpBill {
  id: number; invoice_number: string; customer_id: number; customer_name: string; first_name: string | null; is_commercial: boolean;
  total: number; paid: number; balance: number;
  sent_at: string | null; due_date: string | null; days_open: number; due: string | null;
  last_reminder_at: string | null; reminded: string | null; reminders: number;
  to: string | null; autopay: boolean; autopay_note: string | null; can: boolean; why: string | null; ticked: boolean;
}
interface FollowUpData {
  bills: FollowUpBill[]; sms_on: boolean;
  totals: { bills: number; balance: number; ticked: number; ticked_balance: number };
}
interface SendResult {
  id: number; invoice_number: string | null; customer_name: string | null; balance: number;
  sent: boolean; to: string | null; skipped: string | null; error: string | null;
}

const money = (n: any) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
/** "(512) 555-0101" from whatever is on file; anything that is not ten digits is shown as typed. */
const phoneWords = (p: string | null) => {
  const d = String(p || '').replace(/\D/g, '').replace(/^1(\d{10})$/, '$1');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(p || '');
};
/** "sent 12 days ago" (today → "sent today"), then "due Oct 5" / "past due" when the bill has a due date. */
const openWords = (b: FollowUpBill) => {
  const sent = b.days_open === 0 ? 'sent today' : `sent ${plural(b.days_open, 'day')} ago`;
  return b.due ? `${sent} · ${b.due}` : sent;
};
/** The small grey note under a row — why it is unticked, or why it cannot be. */
const noteWords = (b: FollowUpBill) => {
  const parts: string[] = [];
  if (b.reminded) parts.push(b.reminded);
  if (b.autopay_note) parts.push(b.autopay_note);   // "autopay — the card didn't go through" / "autopay — not charged"; the server knows the Stripe record
  if (!b.can && b.why) parts.push(b.why);
  return parts.join(' · ');
};
/** Pure: the folded line — "3 open bills, oldest 18 days · $412.00 owed"; "No bills open." when the list is empty. */
export const foldWords = (bills: { days_open: number }[], balance: number): string => {
  if (!bills.length) return 'No bills open.';
  const oldest = Math.max(0, ...bills.map(b => Number(b.days_open) || 0));
  const age = oldest === 0 ? 'all sent today' : `oldest ${plural(oldest, 'day')}`;
  return `${plural(bills.length, 'open bill')}, ${age} · ${money(balance)} owed`;
};
const resultWords = (r: SendResult): { text: string; tone: 'ok' | 'warn' | 'bad' } => {
  if (r.sent) return { text: `Texted to ${phoneWords(r.to)}`, tone: 'ok' };
  if (r.error) return { text: `Could not send — ${r.error}`, tone: 'bad' };
  return { text: `Not sent — ${r.skipped || 'nothing to send'}`, tone: 'warn' };
};

export default function FollowUpsCard({ onOpen }: { onOpen: (invoiceId: number) => void }) {
  const qc = useQueryClient();
  const [ticks, setTicks] = useState<Record<number, boolean>>({});
  const [results, setResults] = useState<SendResult[] | null>(null);

  const { data, isLoading } = useQuery('billing-follow-ups',
    () => api.get('/billing/follow-ups').then(r => r.data as FollowUpData),
    { retry: false });
  const bills = useMemo(() => data?.bills || [], [data]);

  // A bill new to the list starts ticked as the server says; one he already ticked or unticked keeps his choice.
  useEffect(() => {
    setTicks(prev => {
      const next: Record<number, boolean> = {};
      for (const b of bills) next[b.id] = b.id in prev ? prev[b.id] : b.ticked;
      return next;
    });
  }, [bills]);

  const chosen = bills.filter(b => ticks[b.id] && b.can);
  const chosenBalance = chosen.reduce((t, b) => t + (Number(b.balance) || 0), 0);

  const send = useMutation((picked: FollowUpBill[]) => api.post('/billing/follow-ups/send', { ids: picked.map(b => b.id) }).then(r => r.data), {
    onSuccess: (d: any) => {
      const res: SendResult[] = d?.results || [];
      setResults(res);
      const bad = res.some(r => !r.sent);
      (bad ? toast.warn : toast.success)(d?.message || 'Sent', { autoClose: 9000 });
      // What was just reminded starts unticked next time; the lists and the dashboard read the fresh logs.
      setTicks({});
      qc.invalidateQueries('billing-follow-ups'); qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || 'Could not send the reminders');
      qc.invalidateQueries('billing-follow-ups');
    },
  });

  if (isLoading || !data) return null;
  if (!bills.length && !results) return null;

  const sentCount = results ? results.filter(r => r.sent).length : 0;
  const failedCount = results ? results.filter(r => !r.sent && r.error).length : 0;
  const skippedCount = results ? results.length - sentCount - failedCount : 0;

  return (
    <FoldCard storageKey="billing_fold_reminders" icon={<Bell className="h-4 w-4 text-amber-600" />} title="Payment reminders"
      summary={<>{foldWords(bills, data.totals.balance)}{bills.length ? ' — a friendly text with the pay link, to the ones you tick.' : ''}</>}>
      {bills.length > 0 && (
        <>
          {!data.sms_on && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Texting isn&apos;t set up on this server — the single-bill Send reminder hands you the text to send yourself.
            </p>
          )}
          <div className="flex items-center gap-3 text-xs">
            <button type="button" className="text-brand-700 font-medium underline"
              onClick={() => setTicks(Object.fromEntries(bills.map(b => [b.id, b.can])))}>Tick all</button>
            <button type="button" className="text-gray-600 font-medium underline"
              onClick={() => setTicks(Object.fromEntries(bills.map(b => [b.id, false])))}>Untick all</button>
          </div>
          <ul className="divide-y divide-gray-100 border-t border-b border-gray-100">
            {bills.map(b => {
              const note = noteWords(b);
              return (
                <li key={b.id} className="py-2.5">
                  <div className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1 h-5 w-5 flex-shrink-0 disabled:opacity-40" checked={!!ticks[b.id] && b.can} disabled={!b.can}
                      aria-label={`Remind ${b.customer_name}`}
                      onChange={e => setTicks(t => ({ ...t, [b.id]: e.target.checked }))} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <button type="button" onClick={() => onOpen(b.id)} className="text-sm font-semibold text-gray-900 hover:underline truncate text-left">
                          {b.customer_name}
                        </button>
                        <p className="text-sm font-bold text-gray-900 flex-shrink-0">{money(b.balance)}</p>
                      </div>
                      <p className="text-xs text-gray-600">
                        <span className="text-gray-500">{b.invoice_number}</span> · {openWords(b)}
                        {b.paid > 0 ? ` · ${money(b.paid)} paid of ${money(b.total)}` : ''}
                      </p>
                      {note && <p className={`text-xs ${b.can ? 'text-gray-500' : 'text-amber-800'}`}>{note}</p>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <button type="button" disabled={!chosen.length || !data.sms_on || send.isLoading}
            onClick={() => {
              if (window.confirm(`Text ${plural(chosen.length, 'reminder')} (${money(chosenBalance)} still owed) now? Each customer gets a friendly text with their pay link.`)) {
                send.mutate(chosen);
              }
            }}
            className="press w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-50">
            {send.isLoading ? 'Sending…' : chosen.length ? `Send ${plural(chosen.length, 'reminder')} (${money(chosenBalance)})` : 'Tick the bills to remind'}
          </button>
        </>
      )}

      {results && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-bold text-gray-900">Sent {sentCount} · skipped {skippedCount}{failedCount > 0 ? ` · ${failedCount} could not be sent` : ''}</p>
            <button type="button" onClick={() => setResults(null)} className="text-xs text-gray-600 underline">Done</button>
          </div>
          <ul className="space-y-1">
            {results.map(r => {
              const w = resultWords(r);
              return (
                <li key={r.id} className="text-xs">
                  <button type="button" onClick={() => onOpen(r.id)} className="font-semibold text-gray-900 hover:underline">{r.customer_name || `Bill #${r.id}`}</button>
                  <span className="text-gray-500"> · {r.invoice_number || '—'} · {money(r.balance)}</span>
                  <span className={`block ${w.tone === 'ok' ? 'text-emerald-700' : w.tone === 'bad' ? 'text-red-700' : 'text-amber-800'}`}>{w.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </FoldCard>
  );
}
